import { useCallback, useEffect, useRef } from 'react';
import { useChatStore, type ChatState, type ToolCallEvent } from '../stores/chatStore';
import { executeToolCall } from '../tools';
import { getSupabaseAccessToken } from '@bastion/data';
import type { StoreApi, UseBoundStore } from 'zustand';

const CHAT_API_URL =
  (import.meta.env.VITE_BASTION_API_URL || 'http://localhost:8000').replace(
    /\/+$/,
    ''
  ) + '/chat';

const SIDE_EFFECT_TOOL_NAMES = new Set([
  'navigate_to_tab',
  'navigate_to_dataset',
  'navigate_to_experiment',
  'navigate_to_workflow_run',
  'set_episode_filters',
  'clear_episode_filters',
  'set_collection_task',
  'set_collection_presets',
  'set_workflow_filters',
]);

const RENDER_ONLY_TOOL_NAMES = new Set([
  'ask_user_choice',
  'render_workflow_results',
]);

type PendingDashboardArtifactAction = {
  toolId: string;
  apply: () => void;
};

/** Human-readable targets for navigate_to_tab (web + legacy desktop tab ids). */
const NAVIGATE_TAB_LABELS: Record<string, string> = {
  chat: 'Chat',
  home: 'Chat',
  'data-studio': 'Data Studio',
  episodes: 'Episodes',
  datasets: 'Datasets',
  'ml-studio': 'ML Studio',
  analytics: 'Analytics',
  dashboards: 'Dashboards',
  'collector-insights': 'Collectors',
  fleet: 'Fleet',
  telemetry: 'Telemetry',
  devservers: 'Devservers',
  settings: 'Settings',
};

function confirmationCopyForTool(name: string, args: Record<string, unknown>): string {
  switch (name) {
    case 'navigate_to_tab': {
      const tabId = String(args.tab_id ?? '');
      const label = (NAVIGATE_TAB_LABELS[tabId] ?? tabId) || 'another page';
      return `Navigate to ${label}?`;
    }
    case 'navigate_to_dataset':
      return 'Open this dataset page?';
    case 'navigate_to_workflow_run':
      return 'Open this workflow run?';
    case 'navigate_to_experiment':
      return 'Open this workflow run?';
    case 'set_episode_filters':
      return 'Apply these episode filters and move to Episodes?';
    case 'clear_episode_filters':
      return 'Clear the episode filters?';
    case 'set_collection_task':
    case 'set_collection_presets':
      return 'Update collection settings?';
    case 'set_workflow_filters': {
      const summary = typeof args.summary === 'string' && args.summary.trim()
        ? args.summary.trim()
        : '';
      return summary
        ? `Apply these workflow filters and open Workflows? (${summary})`
        : 'Apply these workflow filters and open Workflows?';
    }
    default:
      return 'Let Gauss perform this action?';
  }
}

function parseSSELine(line: string): { event?: string; data?: string } | null {
  if (line.startsWith('event: ')) {
    return { event: line.slice(7).trim() };
  }
  if (line.startsWith('data: ')) {
    return { data: line.slice(6) };
  }
  return null;
}

/**
 * Hook for managing chat with the assistant via SSE streaming.
 *
 * Text deltas are dispatched to the store synchronously per chunk — Zustand
 * notifies subscribers immediately, React re-renders, and the trailing live
 * text part is the only thing that re-parses (earlier parts are memoized),
 * so per-token rendering stays cheap.
 *
 * @param getAppState - Optional function that returns the current app state snapshot
 * @param store - Optional Zustand store to use (defaults to the main useChatStore)
 */
export function useChat(
  getAppState?: () => Record<string, unknown>,
  store?: UseBoundStore<StoreApi<ChatState>>,
) {
  const useStore = store ?? useChatStore;
  const storeState = useStore();
  const abortRef = useRef<AbortController | null>(null);
  const dashboardArtifactConfirmedRef = useRef(false);
  const pendingDashboardArtifactActionsRef = useRef<PendingDashboardArtifactAction[]>([]);
  const proposeWidgetToolIdsRef = useRef<string[]>([]);
  const proposeDashboardToolIdsRef = useRef<string[]>([]);
  const activeProposeWidgetToolIdRef = useRef<string | null>(null);
  const activeProposeDashboardToolIdRef = useRef<string | null>(null);
  const pendingConfirmationsRef = useRef<
    Map<string, { confirm: () => void; cancel: () => void }>
  >(new Map());

  const storeRef = useRef(useStore);
  storeRef.current = useStore;

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    const state = storeRef.current.getState();
    const {
      addUserMessage,
      addAssistantMessage,
      addToolCall,
      updateToolCallStatus,
      setToolCallConfirmation,
      attachToolResult,
      setStreaming,
      setError,
    } = state;

    for (const message of state.messages) {
      for (const toolCall of message.toolCalls ?? []) {
        if (toolCall.status === 'awaiting_confirmation') {
          updateToolCallStatus(message.id, toolCall.id, 'cancelled');
        }
      }
    }
    pendingConfirmationsRef.current.clear();

    addUserMessage(content);
    dashboardArtifactConfirmedRef.current = false;
    pendingDashboardArtifactActionsRef.current = [];
    proposeWidgetToolIdsRef.current = [];
    proposeDashboardToolIdsRef.current = [];
    activeProposeWidgetToolIdRef.current = null;
    activeProposeDashboardToolIdRef.current = null;
    const assistantId = addAssistantMessage();
    setStreaming(true);
    setError(null);

    const confirmDashboardArtifacts = () => {
      dashboardArtifactConfirmedRef.current = true;
      const actions = pendingDashboardArtifactActionsRef.current;
      pendingDashboardArtifactActionsRef.current = [];
      for (const action of actions) {
        action.apply();
        updateToolCallStatus(assistantId, action.toolId, 'executed');
        pendingConfirmationsRef.current.delete(action.toolId);
      }
    };

    const cancelDashboardArtifacts = () => {
      const actions = pendingDashboardArtifactActionsRef.current;
      pendingDashboardArtifactActionsRef.current = [];
      for (const action of actions) {
        updateToolCallStatus(assistantId, action.toolId, 'cancelled');
        pendingConfirmationsRef.current.delete(action.toolId);
      }
    };

    const queueDashboardArtifactAction = (
      toolId: string,
      confirmation: NonNullable<ToolCallEvent['confirmation']>,
      apply: () => void,
    ) => {
      pendingDashboardArtifactActionsRef.current.push({ toolId, apply });
      setToolCallConfirmation(assistantId, toolId, confirmation);
      pendingConfirmationsRef.current.set(toolId, {
        confirm: confirmDashboardArtifacts,
        cancel: cancelDashboardArtifacts,
      });
    };

    const consumeDashboardToolId = (
      queueRef: { current: string[] },
      activeRef: { current: string | null },
    ): string | null => {
      const next = queueRef.current.shift();
      if (next) {
        activeRef.current = next;
        return next;
      }
      return activeRef.current;
    };

    let unresolvedDashboardToolsClosed = false;
    const closeUnresolvedDashboardTools = (
      status: Extract<ToolCallEvent['status'], 'cancelled' | 'error'>,
    ) => {
      if (unresolvedDashboardToolsClosed) return;
      unresolvedDashboardToolsClosed = true;
      for (const toolId of [
        ...proposeWidgetToolIdsRef.current,
        ...proposeDashboardToolIdsRef.current,
      ]) {
        updateToolCallStatus(assistantId, toolId, status);
        pendingConfirmationsRef.current.delete(toolId);
      }
      proposeWidgetToolIdsRef.current = [];
      proposeDashboardToolIdsRef.current = [];
    };

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      const token = await getSupabaseAccessToken();
      const appState = getAppState ? getAppState() : {};
      const response = await fetch(CHAT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          new_message: content,
          app_state: appState,
          session_id: storeRef.current.getState().sessionId,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Chat request failed: ${response.status} ${errorText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = 'text_delta';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) {
            currentEvent = 'text_delta';
            continue;
          }

          const parsed = parseSSELine(trimmed);
          if (!parsed) continue;

          if (parsed.event) {
            currentEvent = parsed.event;
            continue;
          }

          if (parsed.data !== undefined) {
            try {
              const payload = JSON.parse(parsed.data);

              switch (currentEvent) {
                case 'text_delta': {
                  const delta = payload.content ?? '';
                  if (delta) {
                    storeRef.current
                      .getState()
                      .appendToAssistant(assistantId, delta);
                  }
                  break;
                }

                case 'tool_call': {
                  const toolName = String(payload.name ?? '');
                  const toolArgs = (payload.args ?? {}) as Record<string, unknown>;
                  const isSideEffectTool = SIDE_EFFECT_TOOL_NAMES.has(toolName);
                  const isRenderOnlyTool = RENDER_ONLY_TOOL_NAMES.has(toolName);
                  const toolCallEvent: ToolCallEvent = {
                    id: payload.id,
                    name: toolName,
                    args: toolArgs,
                    status: isSideEffectTool
                      ? 'awaiting_confirmation'
                      : isRenderOnlyTool
                        ? 'rendered'
                        : 'pending',
                    ...(isSideEffectTool
                      ? {
                          confirmation: {
                            message: confirmationCopyForTool(toolName, toolArgs),
                            confirmLabel: 'Confirm',
                            cancelLabel: 'Cancel',
                          },
                        }
                      : {}),
                  };
                  addToolCall(assistantId, toolCallEvent);

                  try {
                    if (isSideEffectTool) {
                      pendingConfirmationsRef.current.set(payload.id, {
                        confirm: () => {
                          try {
                            executeToolCall(toolName, toolArgs);
                            updateToolCallStatus(assistantId, payload.id, 'executed');
                          } catch (err) {
                            console.error('[useChat] Tool execution error:', err);
                            updateToolCallStatus(assistantId, payload.id, 'error');
                          } finally {
                            pendingConfirmationsRef.current.delete(payload.id);
                          }
                        },
                        cancel: () => {
                          pendingConfirmationsRef.current.delete(payload.id);
                          updateToolCallStatus(assistantId, payload.id, 'cancelled');
                        },
                      });
                      break;
                    }
                    if (isRenderOnlyTool) {
                      break;
                    }
                    if (toolName === 'propose_widget') {
                      proposeWidgetToolIdsRef.current.push(payload.id);
                    } else if (toolName === 'propose_dashboard') {
                      proposeDashboardToolIdsRef.current.push(payload.id);
                    }
                    if (toolName !== 'propose_widget' && toolName !== 'propose_dashboard') {
                      executeToolCall(toolName, toolArgs);
                      updateToolCallStatus(assistantId, payload.id, 'executed');
                    }
                  } catch (err) {
                    console.error('[useChat] Tool execution error:', err);
                    updateToolCallStatus(assistantId, payload.id, 'error');
                  }
                  break;
                }

                case 'tool_confirm': {
                  const pending = pendingConfirmationsRef.current.get(payload.id);
                  pending?.confirm();
                  break;
                }

                case 'tool_cancel': {
                  const pending = pendingConfirmationsRef.current.get(payload.id);
                  pending?.cancel();
                  break;
                }

                case 'widget_proposed': {
                  // Streaming hint from `propose_widget`. The store
                  // overlays this onto any committed proposal so the
                  // right-side artifact panel fills in tile-by-tile
                  // before the canonical `dashboard_proposed` event.
                  const position =
                    typeof payload.position === 'number' &&
                    Number.isFinite(payload.position)
                      ? Math.max(0, Math.floor(payload.position))
                      : null;
                  const widget = payload.widget;
                  const toolId = consumeDashboardToolId(
                    proposeWidgetToolIdsRef,
                    activeProposeWidgetToolIdRef,
                  );
                  if (position !== null && widget && typeof widget === 'object') {
                    if (!dashboardArtifactConfirmedRef.current && toolId) {
                      queueDashboardArtifactAction(
                        toolId,
                        {
                          message: 'Start building a dashboard preview on the right?',
                          confirmLabel: 'Build dashboard',
                          cancelLabel: 'Cancel',
                        },
                        () =>
                          storeRef.current
                            .getState()
                            .upsertStreamingWidget(position, widget),
                      );
                      break;
                    }
                    storeRef.current
                      .getState()
                      .upsertStreamingWidget(position, widget);
                    if (toolId) {
                      updateToolCallStatus(assistantId, toolId, 'executed');
                    }
                  }
                  break;
                }

                case 'dashboard_proposed': {
                  // Canonical commit from `propose_dashboard`. The
                  // store clears the streaming overlay so we don't
                  // shadow the committed widget order.
                  const title =
                    typeof payload.title === 'string' ? payload.title : '';
                  const description =
                    payload.description === null ||
                    payload.description === undefined
                      ? null
                      : String(payload.description);
                  const definition = payload.definition;
                  const explanation =
                    typeof payload.explanation === 'string'
                      ? payload.explanation
                      : '';
                  if (
                    definition &&
                    typeof definition === 'object' &&
                    Array.isArray((definition as { widgets?: unknown[] }).widgets)
                  ) {
                    const toolId = consumeDashboardToolId(
                      proposeDashboardToolIdsRef,
                      activeProposeDashboardToolIdRef,
                    );
                    const apply = () =>
                      storeRef.current.getState().setDashboardProposal({
                        title,
                        description,
                        definition: definition as {
                          widgets: unknown[];
                          parameters?: unknown[];
                        },
                        explanation,
                        ...(typeof payload.is_public === 'boolean'
                          ? { is_public: payload.is_public }
                          : {}),
                      });

                    if (!dashboardArtifactConfirmedRef.current && toolId) {
                      queueDashboardArtifactAction(
                        toolId,
                        {
                          message: 'Show this dashboard preview on the right?',
                          confirmLabel: 'Show preview',
                          cancelLabel: 'Cancel',
                        },
                        apply,
                      );
                      break;
                    }
                    apply();
                    if (toolId) {
                      updateToolCallStatus(assistantId, toolId, 'executed');
                    }
                  }
                  break;
                }
                case 'tool_result': {
                  attachToolResult(
                    assistantId,
                    payload.id,
                    payload.preview ?? '',
                    {
                      truncated: payload.truncated ?? false,
                      fullLength: payload.full_length,
                    },
                  );
                  break;
                }

                case 'error': {
                  setError(payload.message ?? 'Unknown error');
                  break;
                }

                case 'done': {
                  break;
                }
              }
            } catch {
              // Ignore JSON parse errors for partial data
            }
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        closeUnresolvedDashboardTools('cancelled');
        return;
      }
      closeUnresolvedDashboardTools('error');
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[useChat] Error:', errorMessage);
      storeRef.current.getState().setError(errorMessage);
    } finally {
      closeUnresolvedDashboardTools('error');
      // Only clean up if this is still the active request (a newer
      // sendMessage call may have replaced abortRef already).
      const isStillActive = abortRef.current === abortController;
      if (isStillActive) {
        storeRef.current.getState().setStreaming(false);
        abortRef.current = null;
      }
    }
  }, [getAppState]);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      storeRef.current.getState().setStreaming(false);
    }
  }, []);

  const confirmToolCall = useCallback((toolCallId: string) => {
    pendingConfirmationsRef.current.get(toolCallId)?.confirm();
  }, []);

  const cancelToolCall = useCallback((toolCallId: string) => {
    pendingConfirmationsRef.current.get(toolCallId)?.cancel();
  }, []);

  /**
   * Reply to a custom tool renderer (e.g. ChoiceCard pick). Marks the
   * matching tool call executed so its UI freezes the answer, then sends
   * the answer text as the next user message. The server already returned
   * a `tool_result` for the original render-only tool call, so the model
   * sees the answer as a regular user turn.
   */
  const answerToolCall = useCallback(
    (toolCallId: string, answerText: string) => {
      const state = storeRef.current.getState();
      for (const message of state.messages) {
        if (message.toolCalls?.some((tc) => tc.id === toolCallId)) {
          state.updateToolCallStatus(message.id, toolCallId, 'executed');
          break;
        }
      }
      sendMessage(answerText);
    },
    [sendMessage],
  );

  return {
    messages: storeState.messages,
    isStreaming: storeState.isStreaming,
    error: storeState.error,
    sessionId: storeState.sessionId,
    sendMessage,
    stopStreaming,
    clearMessages: storeState.clearMessages,
    setSessionId: storeState.setSessionId,
    // Dashboard artifact state — populated by `propose_widget` /
    // `propose_dashboard` SSE events from the server. Webapp layer
    // composes this into the right-side artifact panel slot on
    // AssistantWorkspace.
    dashboardProposal: storeState.dashboardProposal,
    streamingWidgets: storeState.streamingWidgets,
    clearDashboardArtifact: storeState.clearDashboardArtifact,
    confirmToolCall,
    cancelToolCall,
    answerToolCall,
  };
}
